import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider
} from '@mui/material';

interface Props {
  open: boolean;
  onClose: () => void;
}

interface Row {
  keys: string[];
  label: string;
}

const NAV: Row[] = [
  { keys: ['g', 'p'], label: 'Go to Projects' },
  { keys: ['g', 't'], label: 'Go to Tasks' },
  { keys: ['g', 'k'], label: 'Go to Tickets' },
  { keys: ['g', 'c'], label: 'Go to Clients' },
  { keys: ['g', 'm'], label: 'Go to My Assignments' },
  { keys: ['g', 'a'], label: 'Go to Calendar' }
];

const ACTIONS: Row[] = [
  { keys: ['⌘', 'K'], label: 'Open command palette (entity search + create / navigate actions)' },
  { keys: ['/'], label: 'Focus the topbar search' },
  { keys: ['?'], label: 'Open this shortcuts dialog' },
  { keys: ['Esc'], label: 'Close any open dialog' }
];

const Kbd: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    component="kbd"
    sx={{
      display: 'inline-block',
      minWidth: 22,
      px: 0.75,
      py: 0.25,
      fontSize: '0.75rem',
      lineHeight: 1.2,
      fontFamily: 'monospace',
      textAlign: 'center',
      border: 1,
      borderColor: 'divider',
      borderRadius: 0.75,
      bgcolor: 'action.hover',
      color: 'text.primary'
    }}
  >
    {children}
  </Box>
);

const Section: React.FC<{ title: string; rows: Row[] }> = ({ title, rows }) => (
  <Box sx={{ mb: 2 }}>
    <Typography variant="overline" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
      {title}
    </Typography>
    <Box sx={{ mt: 0.5 }}>
      {rows.map((r) => (
        <Box
          key={r.label}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            py: 0.75,
            borderBottom: 1,
            borderColor: 'divider',
            '&:last-child': { borderBottom: 0 }
          }}
        >
          <Typography variant="body2">{r.label}</Typography>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {r.keys.map((k, i) => (
              <Kbd key={i}>{k}</Kbd>
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  </Box>
);

const KeyboardShortcutsDialog: React.FC<Props> = ({ open, onClose }) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Keyboard shortcuts</DialogTitle>
      <DialogContent dividers>
        <Section title="Navigation (press g, then second key)" rows={NAV} />
        <Divider sx={{ my: 1 }} />
        <Section title="Actions" rows={ACTIONS} />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Shortcuts are disabled while you're typing in an input field.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">Got it</Button>
      </DialogActions>
    </Dialog>
  );
};

export default KeyboardShortcutsDialog;
